import debounce from '../util/debounce';

import {Accordion} from './accordion';
import {RangeSlider} from './range-slider';
import {makeGridSwatches} from './swatch';
import {Filters} from './collection-filters-sidebar';
import {Siblings} from './siblings';
import {makeSwappers} from './swapper';

const selectors = {
  collectionSidebar: '[data-collection-sidebar]',
  form: '[data-sidebar-filter-form]',
  inputs: 'input, select, label, textarea',
  priceMin: 'data-field-price-min',
  priceMax: 'data-field-price-max',
  rangeMin: 'data-se-min-value',
  rangeMax: 'data-se-max-value',
  rangeMinDefault: 'data-se-min',
  rangeMaxDefault: 'data-se-max',
  productsContainer: '[data-products-grid]',
  filterUpdateUrlButton: '[data-filter-update-url]',
  activeFiltersHolder: '[data-active-filters]',
  activeFiltersCount: '[data-active-filters-count]',
  productsCount: '[data-products-count]',
  dataSort: 'data-sort-enabled',
  collectionTools: '[data-collection-tools]',
  filtersWrappper: '[data-filters]',
  headerSticky: '[data-header-sticky="sticky"]',
  headerHeight: '[data-header-height]',
  gridLarge: 'data-grid-large',
  gridSmall: 'data-grid-small',
  accordionBody: '[data-accordion-body]',
  checkedOption: 'input:checked',
  optionHolder: '[data-option-holder]',
};

const classes = {
  classHidden: 'is-hidden',
  classLoading: 'is-loading',
};

const times = {
  loadingDelay: 1000,
  scrollTime: 1000,
};

class FiltersForm {
  constructor(section) {
    this.section = section;
    this.container = this.section.container;
    this.sidebar = this.container.querySelector(selectors.collectionSidebar);
    this.form = section.container.querySelector(selectors.form);
    this.sort = this.container.querySelector(`[${selectors.dataSort}]`);
    this.productsContainer = this.container.querySelector(selectors.productsContainer);
    this.activeFiltersHolder = this.container.querySelector(selectors.activeFiltersHolder);
    this.activeFiltersCount = this.container.querySelector(selectors.activeFiltersCount);
    this.productsCount = this.container.querySelector(selectors.productsCount);
    this.headerIsSticky = document.querySelector(selectors.headerSticky) !== null;
    this.layoutLarge = this.container.querySelector(`[${selectors.gridLarge}]`);
    this.layoutSmall = this.container.querySelector(`[${selectors.gridSmall}]`);

    if (this.productsContainer && this.sidebar) {
      this.init();
    }

    if (this.sort) {
      this.container.addEventListener('theme:form:filter', (e) => this.filtering(e));
    }

    if (this.sidebar || this.sort) {
      window.addEventListener('popstate', (e) => this.filtering(e));
    }
  }

  init() {
    this.showAllOptions();

    if (this.form) {
      new RangeSlider(this.form);

      this.sidebar.addEventListener(
        'input',
        debounce((e) => {
          const type = e.type;
          const target = e.target;

          if (!selectors.inputs.includes(type) || !this.form || typeof this.form.submit !== 'function') {
            return;
          }

          const priceMin = this.form.querySelector(`[${selectors.priceMin}]`);
          const priceMax = this.form.querySelector(`[${selectors.priceMax}]`);

          if (target.hasAttribute(selectors.priceMin) || target.hasAttribute(selectors.priceMax)) {
            const rangeMin = this.form.querySelector(`[${selectors.rangeMin}]`);
            const rangeMax = this.form.querySelector(`[${selectors.rangeMax}]`);
            const rangeMinDefault = parseInt(rangeMin.getAttribute(selectors.rangeMinDefault));
            const rangeMaxDefault = parseInt(rangeMax.getAttribute(selectors.rangeMaxDefault));

            if (priceMin.value && !priceMax.value) {
              priceMax.value = rangeMaxDefault;
            }

            if (priceMax.value && !priceMin.value) {
              priceMin.value = rangeMinDefault;
            }

            if (priceMin.value <= rangeMinDefault && priceMax.value >= rangeMaxDefault) {
              priceMin.placeholder = rangeMinDefault;
              priceMax.placeholder = rangeMaxDefault;
              priceMin.value = '';
              priceMax.value = '';
            }
          }

          this.filtering(e);
        }, 1500)
      );

      this.form.addEventListener('theme:range:update', (e) => this.updateRange(e));
    }

    if (this.sidebar) {
      this.sidebar.addEventListener('click', (e) => this.updateFilterFromUrl(e));
    }

    if (this.activeFiltersHolder) {
      this.activeFiltersHolder.addEventListener('click', (e) => this.updateFilterFromUrl(e));
    }

    if (this.productsContainer) {
      this.productsContainer.addEventListener('click', (e) => this.updateFilterFromUrl(e));
    }

    // Color swatches tooltips
    makeSwappers(this.sidebar);
  }

  /**
   * Update range slider (price money)
   * @param {Object} e
   */
  updateRange(e) {
    if (this.form && typeof this.form.submit === 'function') {
      const rangeMin = this.form.querySelector(`[${selectors.rangeMin}]`);
      const rangeMax = this.form.querySelector(`[${selectors.rangeMax}]`);
      const priceMin = this.form.querySelector(`[${selectors.priceMin}]`);
      const priceMax = this.form.querySelector(`[${selectors.priceMax}]`);
      const checkElements = rangeMin && rangeMax && priceMin && priceMax;

      if (checkElements && rangeMin.hasAttribute(selectors.rangeMin) && rangeMax.hasAttribute(selectors.rangeMax)) {
        const priceMinValue = parseInt(priceMin.placeholder);
        const priceMaxValue = parseInt(priceMax.placeholder);
        const rangeMinValue = parseInt(rangeMin.getAttribute(selectors.rangeMin));
        const rangeMaxValue = parseInt(rangeMax.getAttribute(selectors.rangeMax));

        if (priceMinValue !== rangeMinValue || priceMaxValue !== rangeMaxValue) {
          priceMin.value = rangeMinValue;
          priceMax.value = rangeMaxValue;

          this.filtering(e);
        }
      }
    }
  }

  /**
   * Update filter on last clicked element
   * @param {Object} e
   */
  updateFilterFromUrl(e) {
    const target = e.target;
    if (target.matches(selectors.filterUpdateUrlButton) || (target.closest(selectors.filterUpdateUrlButton) && target)) {
      e.preventDefault();
      const button = target.matches(selectors.filterUpdateUrlButton) ? target : target.closest(selectors.filterUpdateUrlButton);
      this.filtering(e, button.getAttribute('href'));
    }
  }

  /**
   * Add filters to history api
   *
   * @param {Object} e
   * @param {String} replaceHref
   */
  addToHistory(e, replaceHref = '') {
    const sortValue = this.sort ? this.sort.getAttribute(selectors.dataSort) : '';

    if (!e || (e && e.type !== 'popstate')) {
      if (replaceHref === '') {
        const searchParams = new window.URL(window.location.href).searchParams;
        const filterUrlParams = Object.fromEntries(searchParams);
        const filterUrlRemoveString = searchParams.toString();

        if (filterUrlRemoveString.includes('filter.') || filterUrlRemoveString.includes('sort_by') || filterUrlRemoveString.includes('page')) {
          for (const key in filterUrlParams) {
            if (key.includes('filter.') || key.includes('sort_by') || key.includes('page')) {
              searchParams.delete(key);
            }
          }
        }

        if (this.form) {
          const formParams = new URLSearchParams(new FormData(this.form));

          for (let [key, val] of formParams.entries()) {
            if (key.includes('filter.') && val) {
              searchParams.append(key, val);
            }
          }

          // if submitted price equal to price range min and max remove price parameters
          const rangeMin = this.form.querySelector(`[${selectors.rangeMin}]`);
          const rangeMax = this.form.querySelector(`[${selectors.rangeMax}]`);
          const priceMin = this.form.querySelector(`[${selectors.priceMin}]`);
          const priceMax = this.form.querySelector(`[${selectors.priceMax}]`);
          const checkElements = rangeMin && rangeMax && priceMin && priceMax;

          if (checkElements && rangeMin.hasAttribute(selectors.rangeMinDefault) && rangeMax.hasAttribute(selectors.rangeMaxDefault)) {
            const rangeMinDefault = parseFloat(rangeMin.getAttribute(selectors.rangeMinDefault), 10);
            const rangeMaxDefault = parseFloat(rangeMax.getAttribute(selectors.rangeMaxDefault), 10);
            const priceMinValue = priceMin.value === '' ? parseFloat(priceMin.placeholder, 10) : parseFloat(priceMin.value, 10);
            const priceMaxValue = priceMax.value === '' ? parseFloat(priceMax.placeholder, 10) : parseFloat(priceMax.value, 10);

            if (priceMinValue <= rangeMinDefault && priceMaxValue >= rangeMaxDefault) {
              searchParams.delete('filter.v.price.gte');
              searchParams.delete('filter.v.price.lte');
            }
          }
        }

        if (sortValue || (e && e.detail && e.detail.href)) {
          const sortString = sortValue ? sortValue : e.detail.params;
          searchParams.set('sort_by', sortString);
        }

        const filterUrlString = searchParams.toString();
        const filterNewParams = filterUrlString ? `?${filterUrlString}` : location.pathname;
        window.history.pushState(null, '', filterNewParams);

        return;
      }

      window.history.pushState(null, '', replaceHref);
    }
  }

  /**
   * Get filter result and append them to their holders
   */
  getFilterResult() {
    this.productsContainer.classList.add(classes.classLoading);
    this.getGridValues();

    // use the section_id to target the collection section and exclude other sections on the page
    const url = new window.URL(window.location.href);
    const params = url.searchParams;
    params.set('section_id', this.section.id);

    fetch(`${window.location.pathname}${url.search}`)
      .then((response) => response.text())
      .then((response) => {
        const responseHolder = document.createElement('div');
        responseHolder.innerHTML = response;

        if (this.sidebar) {
          this.sidebar.innerHTML = responseHolder.querySelector(selectors.collectionSidebar).innerHTML;
        }

        if (this.activeFiltersCount) {
          this.activeFiltersCount.innerHTML = responseHolder.querySelector(selectors.activeFiltersCount).innerHTML;
        }

        this.productsContainer.innerHTML = responseHolder.querySelector(selectors.productsContainer).innerHTML;

        // Show active filters holder
        this.activeFiltersHolder.innerHTML = responseHolder.querySelector(selectors.activeFiltersHolder).innerHTML;
        this.activeFiltersHolder.parentNode.classList.toggle(classes.classHidden, this.activeFiltersHolder.innerHTML === '');

        if (this.productsCount) {
          this.productsCount.innerHTML = responseHolder.querySelector(selectors.productsCount).innerHTML;
        }

        this.setGridValues();
        this.refreshFunctionalities();

        setTimeout(() => {
          this.productsContainer.classList.remove(classes.classLoading);
        }, times.loadingDelay);
      });
  }

  /**
   * Refresh functionalities
   */
  refreshFunctionalities() {
    // Init range slider
    this.form = this.container.querySelector(selectors.form);
    if (this.form) {
      new RangeSlider(this.form);
      this.form.addEventListener('theme:range:update', (e) => this.updateRange(e));
    }

    // Init filters
    const filters = this.container.querySelectorAll(selectors.filtersWrappper);
    filters.forEach((filter) => {
      new Filters(filter, true);
    });

    // Init accordions
    const accordions = this.container.querySelectorAll(selectors.accordionBody);
    accordions.forEach((accordion) => {
      new Accordion(accordion);
    });

    // Init siblings
    new Siblings(this.container);

    // Color swatches tooltips
    if (this.sidebar) {
      makeSwappers(this.sidebar);
    }

    // Init product swatches
    makeGridSwatches(this.container);

    this.showAllOptions();
  }

  // Get grid values
  getGridValues() {
    if (this.layoutLarge) {
      this.layoutLargeValue = this.layoutLarge.getAttribute(selectors.gridLarge);
    }

    if (this.layoutSmall) {
      this.layoutSmallValue = this.layoutSmall.getAttribute(selectors.gridSmall);
    }
  }

  // Set grid values on AJAX
  setGridValues() {
    if (this.layoutLarge) {
      this.layoutLarge = this.container.querySelector(`[${selectors.gridLarge}]`);
      this.layoutLarge.setAttribute(selectors.gridLarge, this.layoutLargeValue);
    }

    if (this.layoutSmall) {
      this.layoutSmall = this.container.querySelector(`[${selectors.gridSmall}]`);
      this.layoutSmall.setAttribute(selectors.gridSmall, this.layoutSmallValue);
    }
  }

  // Show all options if in the filter have selected option but it is hidden
  showAllOptions() {
    const checkedOptions = this.container.querySelectorAll(selectors.checkedOption);

    checkedOptions.forEach((option) => {
      if (option.closest(selectors.optionHolder) && option.closest(selectors.optionHolder).classList.contains(classes.classHidden)) {
        const button = option.closest(selectors.accordionBody).nextElementSibling;

        if (!button.classList.contains(selectors.classHidden)) {
          button.dispatchEvent(new Event('click'));
        }
      }
    });
  }

  /**
   * Filtering method and scroll at the top on the section
   * @param {Object} e
   * @param {String} replaceHref
   */
  filtering(e, replaceHref = '') {
    // Scroll to filter tools
    const headerH = this.headerIsSticky ? document.querySelector(selectors.headerHeight).getBoundingClientRect().height : 0;
    const scrollToElement = this.container.offsetTop - headerH;

    let options = {
      root: null,
      rootMargin: `${headerH}px`,
      threshold: 1.0,
    };

    const handleFilterAndScroll = (entries) => {
      const [entry] = entries;

      setTimeout(
        () => {
          this.addToHistory(e, replaceHref);
          this.getFilterResult();
        },
        entry.isIntersecting ? 10 : times.scrollTime
      );

      observer.unobserve(this.productsContainer);

      if (entry.isIntersecting) return;

      window.scrollTo({
        top: scrollToElement,
        left: 0,
        behavior: 'smooth',
      });
    };

    const observer = new IntersectionObserver(handleFilterAndScroll, options);

    observer.observe(this.productsContainer);
  }
}

const collectionFiltersForm = {
  onLoad() {
    this.filterForm = new FiltersForm(this);
  },
  onUnload() {
    if (this.filterForm && typeof this.filterForm.unload === 'function') {
      this.filterForm.unload();
    }
  },
};

export default collectionFiltersForm;
